/** Reassembles bounded, ordered DRTC fragments without exposing partial H.264 packets. */
export class RtcPacketAssembler {
  private packet: Uint8Array | null = null;
  private offset = 0;
  reset() { this.packet = null; this.offset = 0; }
  accept(data: ArrayBuffer): ArrayBuffer | null {
    if (data.byteLength <= 12 || data.byteLength > 16384) throw new Error('Invalid RTC fragment size');
    const header = new DataView(data);
    const total = header.getUint32(4);
    const offset = header.getUint32(8);
    const size = data.byteLength - 12;
    if (header.getUint32(0) !== 0x44525443 || total === 0 || total > 8 * 1024 * 1024 || offset + size > total) {
      throw new Error('Invalid RTC fragment header');
    }
    if (offset === 0 && this.packet === null) this.packet = new Uint8Array(total);
    if (!this.packet || this.packet.length !== total || offset !== this.offset) throw new Error('RTC fragment discontinuity');
    this.packet.set(new Uint8Array(data, 12), offset);
    this.offset += size;
    if (this.offset !== total) return null;
    const packet = this.packet.buffer as ArrayBuffer;
    this.reset();
    return packet;
  }
}

interface RtcCallbacks {
  sendSignal: (message: Record<string, unknown>) => void;
  onOpen: () => void;
  onPacket: (packet: ArrayBuffer) => void;
  onClose: () => void;
  onRtt: (rtt: number) => void;
}

/** Host ICE only. No STUN/TURN or RTP media, so the existing canvas decoder is retained. */
export class DirectRtcClient {
  private peer: RTCPeerConnection | null = null;
  private channel: RTCDataChannel | null = null;
  private id: string | null = null;
  private timer: number | null = null;
  private timeout: number | null = null;
  private lastPong = 0;
  private pingSent = 0;
  private pendingIce: RTCIceCandidateInit[] = [];
  private readonly assembler = new RtcPacketAssembler();
  constructor(private readonly callbacks: RtcCallbacks) {}

  async start(stunUrl?: string | null): Promise<void> {
    if (this.peer || typeof RTCPeerConnection === 'undefined') return;
    const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    this.id = id;
    try {
      const iceServers: RTCIceServer[] = [];
      if (stunUrl) iceServers.push({ urls: stunUrl });
      const pc = new RTCPeerConnection({ iceServers });
      this.peer = pc;
      // In-band channel (not negotiated id 0): better QtWebEngine / Tesla compatibility.
      const dc = pc.createDataChannel('drivedeck', { ordered: true });
      this.channel = dc;
      dc.binaryType = 'arraybuffer';
      let offerSent = false;
      const localIce: RTCIceCandidateInit[] = [];
      const sendIce = (ice: RTCIceCandidateInit) => this.callbacks.sendSignal({ type: 'rtc_ice', negotiationId: id, ...ice });
      pc.onicecandidate = event => {
        if (this.peer !== pc || !event.candidate) return;
        const ice = event.candidate.toJSON();
        if (offerSent) sendIce(ice); else localIce.push(ice);
      };
      pc.onconnectionstatechange = () => {
        if (this.peer === pc && ['failed', 'disconnected', 'closed'].includes(pc.connectionState)) this.stop();
      };
      dc.onopen = () => {
        if (this.peer !== pc) return;
        if (this.timeout !== null) clearTimeout(this.timeout);
        this.timeout = null;
        this.lastPong = Date.now();
        const ping = () => {
          if (Date.now() - this.lastPong > 5000 || dc.bufferedAmount > 65536) { this.stop(); return; }
          this.pingSent = Date.now();
          try { dc.send('ping'); } catch { this.stop(); }
        };
        this.timer = window.setInterval(ping, 1000);
        ping();
        this.callbacks.onOpen();
      };
      dc.onmessage = event => {
        if (this.peer !== pc) return;
        if (event.data === 'pong') {
          this.lastPong = Date.now();
          this.callbacks.onRtt(this.lastPong - this.pingSent);
        } else if (event.data instanceof ArrayBuffer) {
          try {
            const packet = this.assembler.accept(event.data);
            if (packet) this.callbacks.onPacket(packet);
          } catch { this.stop(); }
        }
      };
      dc.onclose = dc.onerror = () => { if (this.peer === pc) this.stop(); };
      this.timeout = window.setTimeout(() => {
        if (this.peer === pc) {
          console.warn('[DriveDeck] WebRTC negotiation timed out; staying on RELAY');
          this.stop();
        }
      }, 12000);
      const offer = await pc.createOffer();
      if (this.peer !== pc) return;
      await pc.setLocalDescription(offer);
      if (this.peer !== pc) return;
      this.callbacks.sendSignal({ type: 'rtc_offer', negotiationId: id, sdp: offer.sdp });
      offerSent = true;
      localIce.forEach(sendIce);
      console.log(`[DriveDeck] WebRTC offer sent (in-band data channel, ${stunUrl ? 'STUN+host' : 'host ICE'})`);
    } catch (error) {
      console.warn('[DriveDeck] WebRTC start failed:', error);
      if (this.id === id) this.stop();
    }
  }

  async handleSignal(message: Record<string, unknown>): Promise<void> {
    const pc = this.peer;
    if (!pc || message.negotiationId !== this.id) return;
    try {
      if (message.type === 'rtc_answer' && typeof message.sdp === 'string' && !pc.remoteDescription) {
        await pc.setRemoteDescription({ type: 'answer', sdp: message.sdp });
        if (this.peer !== pc) return;
        for (const ice of this.pendingIce.splice(0)) await pc.addIceCandidate(ice);
      } else if (message.type === 'rtc_ice' && typeof message.candidate === 'string') {
        const ice: RTCIceCandidateInit = {
          candidate: message.candidate,
          sdpMid: typeof message.sdpMid === 'string' ? message.sdpMid : null,
          sdpMLineIndex: typeof message.sdpMLineIndex === 'number' ? message.sdpMLineIndex : 0,
        };
        if (pc.remoteDescription) await pc.addIceCandidate(ice);
        else if (this.pendingIce.length < 64) this.pendingIce.push(ice);
        else this.stop();
      } else if (message.type === 'rtc_close') this.stop(false);
    } catch { if (this.peer === pc) this.stop(); }
  }

  send(packet: ArrayBuffer): boolean {
    const dc = this.channel;
    if (!dc || dc.readyState !== 'open') return false;
    if (dc.bufferedAmount > 65536) { this.stop(); return false; }
    try { dc.send(packet); return true; } catch { this.stop(); return false; }
  }

  stop(notify = true): void {
    const id = this.id;
    this.id = null;
    const peer = this.peer;
    this.peer = null;
    const channel = this.channel;
    this.channel = null;
    if (this.timer !== null) clearInterval(this.timer);
    if (this.timeout !== null) clearTimeout(this.timeout);
    this.timer = this.timeout = null;
    this.pendingIce = [];
    this.assembler.reset();
    if (channel) { channel.onopen = channel.onmessage = channel.onclose = channel.onerror = null; channel.close(); }
    if (peer) { peer.onicecandidate = peer.onconnectionstatechange = null; peer.close(); }
    if (notify && id) this.callbacks.sendSignal({ type: 'rtc_close', negotiationId: id });
    if (id) this.callbacks.onClose();
  }
}
